import React, { useMemo } from 'react';
import './EmailPreviewContent.scss';
import type { TEmailPreviewData } from 'components/FlowBuilder/FlowSidePanels/types';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ErrorIcon from 'assets/icons/icon-error-triangle.svg';
import { processTemplateContent } from '../../shared/utils/emailSMS/template-preview.utils';

const MAIN_CLASS = 'email-preview-content';
const CLASSES = {
  MAPPED_VALUE: `${MAIN_CLASS}__mapped-value`,
  ERROR: `${MAIN_CLASS}__error`,
  SKELETON: `${MAIN_CLASS}__skeleton`,
  SKELETON_HEADER: `${MAIN_CLASS}__skeleton__header`,
  SKELETON_HEADER_ICON: `${MAIN_CLASS}__skeleton__header__icon`,
  SKELETON_HEADER_TEXT: `${MAIN_CLASS}__skeleton__header__text`,
  SKELETON_HEADER_TEXT_SUBJECT: `${MAIN_CLASS}__skeleton__header__text__subject`,
  SKELETON_HEADER_TEXT_PREHEADER: `${MAIN_CLASS}__skeleton__header__text__preheader`,
  SKELETON_BANNER: `${MAIN_CLASS}__skeleton__banner`,
  SKELETON_PARAGRAPHS: `${MAIN_CLASS}__skeleton__paragraphs`,
  SKELETON_CTA: `${MAIN_CLASS}__skeleton__cta`,
  SKELETON_FOOTER: `${MAIN_CLASS}__skeleton__footer`,
};

function decodeHtml(html: string): string {
  try {
    const jsonParsed = JSON.parse(html);
    if (typeof jsonParsed === 'string') {
      return jsonParsed;
    }
  } catch {
    // If JSON.parse fails, it's not a valid JSON string.
    // This is expected if the html is just a plain HTML string.
    // We can proceed with the original html string.
  }

  // Fallback to returning the original string if it's not a JSON-encoded string.
  return html;
}

const EmailPreviewContent: React.FC<TEmailPreviewData> = ({ html, data, isLoading }) => {
  const sanitizedHtml = useMemo(() => {
    if (isLoading) return '';
    const decodedHtml = decodeHtml(html);

    return processTemplateContent({
      content: decodedHtml,
      data: data,
      errorIconSrc: ErrorIcon,
      mappedValueClass: CLASSES.MAPPED_VALUE,
      errorClass: CLASSES.ERROR,
      isHtmlContent: true,
    });
  }, [html, data, isLoading]);

  if (isLoading) {
    return (
      <div className={CLASSES.SKELETON}>
        {/* Header block: single icon + subject + preheader stacked */}
        <div className={CLASSES.SKELETON_HEADER}>
          <div className={CLASSES.SKELETON_HEADER_ICON}>
            <Skeleton height={60} width={60} />
          </div>
          <div className={CLASSES.SKELETON_HEADER_TEXT}>
            <div className={CLASSES.SKELETON_HEADER_TEXT_SUBJECT}>
              <Skeleton height={26} width="55%" />
            </div>
            <div className={CLASSES.SKELETON_HEADER_TEXT_PREHEADER}>
              <Skeleton height={14} width="35%" />
            </div>
          </div>
        </div>
        {/* Banner */}
        <div className={CLASSES.SKELETON_BANNER}>
          <Skeleton height={150} />
        </div>
        {/* Body paragraphs */}
        <div className={CLASSES.SKELETON_PARAGRAPHS}>
          <Skeleton height={14} width="90%" />
          <Skeleton height={14} width="95%" />
          <Skeleton height={14} width="88%" />
          <Skeleton height={14} width="60%" />
        </div>
        {/* CTA Button */}
        <div className={CLASSES.SKELETON_CTA}>
          <Skeleton height={40} width={180} />
        </div>
        {/* Footer lines */}
        <div className={CLASSES.SKELETON_FOOTER}>
          <Skeleton height={12} width="50%" />
          <Skeleton height={12} width="30%" />
        </div>
      </div>
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

export default EmailPreviewContent;
